<template>
  <div class="setting-desktop">
    <aside class="setting-desktop__nav">
      <div class="setting-desktop__profile">
        <v-avatar size="44" color="primary lighten-5">
          <v-img v-if="profileAvatarUrl" :src="profileAvatarUrl" />
          <v-icon v-else color="primary">mdi-storefront-outline</v-icon>
        </v-avatar>
        <div class="setting-desktop__profile-text">
          <div class="setting-desktop__profile-name">
            {{ form.abrName || $t("setting") }}
          </div>
          <div class="setting-desktop__profile-sub">
            {{ form.mobile || form.email || "" }}
          </div>
        </div>
      </div>

      <div class="setting-desktop__nav-scroll">
        <template v-for="group in navGroups">
          <div :key="group.id + '-h'" class="setting-desktop__nav-heading">
            {{ $t(group.title) }}
          </div>
          <button
            v-for="s in group.items"
            :key="s.id"
            type="button"
            class="setting-desktop__nav-item"
            :class="{ 'setting-desktop__nav-item--active': active === s.id }"
            :disabled="s.disabled"
            @click="select(s.id)"
          >
            <v-icon small class="setting-desktop__nav-icon">{{ s.icon }}</v-icon>
            <span class="setting-desktop__nav-label">{{ $t(s.title) }}</span>
            <span v-if="s.count" class="setting-desktop__nav-count">{{ s.count }}</span>
          </button>
        </template>
      </div>
    </aside>

    <section class="setting-desktop__content">
      <!-- Business info (kept mounted so the logo picker survives navigation) -->
      <setting-panel
        v-show="active === 'business'"
        icon="mdi-storefront-outline"
        :title="$t('settingSection_business')"
        :subtitle="$t('settingSection_businessHint')"
      >
        <div class="setting-desktop__business">
          <div class="setting-desktop__logo">
            <file-picker
              ref="refFilePicker"
              v-model="form.imagePicker"
              :label="$t('logo')"
              :avatar="true"
              :multiple="false"
              extensions="image/png, image/gif, image/jpeg"
              type-prop="logo"
              :val="form.imagePickerVal ? form.imagePickerVal : null"
            />
          </div>
          <div class="setting-desktop__grid setting-desktop__grid--2">
            <v-text-field
              v-model="form.abrName"
              :label="$t('abrName')"
              :rules="[(v) => !!v || $t('is-required')]"
              prepend-inner-icon="mdi-store-outline"
              hide-details="auto"
              outlined
              clearable
              dense
            />
            <autocomplete
              v-model="form.bussiness"
              :title="$t('businessType')"
              type="business"
            />
          </div>
        </div>

        <div class="setting-desktop__section-label">{{ $t("settingContactInfo") }}</div>
        <div class="setting-desktop__grid setting-desktop__grid--3">
          <v-text-field
            v-for="f in contactFields"
            :key="f.key"
            v-model="form[f.key]"
            :label="$t(f.label)"
            :prepend-inner-icon="f.icon"
            :inputmode="f.inputmode"
            hide-details
            outlined
            clearable
            dense
          />
          <v-text-field
            v-model="form.address"
            class="setting-desktop__span-all"
            :label="$t('address')"
            prepend-inner-icon="mdi-map-marker-outline"
            hide-details
            outlined
            clearable
            dense
          />
        </div>
        <template #footer>
          <footer-actions />
        </template>
      </setting-panel>

      <setting-panel
        v-if="active === 'details'"
        icon="mdi-book-open-variant"
        :title="$t('settingSection_details')"
        :subtitle="$t('settingSection_detailsHint')"
      >
        <div class="setting-desktop__grid setting-desktop__grid--2">
          <autocomplete
            v-for="f in detailFields"
            :key="f.key"
            v-model="form[f.key]"
            :title="$t(f.label)"
            type="alldetail"
            :value="form[f.key]"
          />
        </div>
        <template #footer>
          <footer-actions />
        </template>
      </setting-panel>

      <template v-for="g in groups">
        <setting-panel
          v-if="active === g.id"
          :key="g.id"
          :icon="g.icon"
          :title="$t(g.title)"
          :subtitle="$t(g.subtitle)"
        >
          <template #actions>
            <v-btn small text color="primary" @click="setAll(g, true)">{{
              $t("settingEnableAll")
            }}</v-btn>
            <v-btn small text @click="setAll(g, false)">{{
              $t("settingDisableAll")
            }}</v-btn>
          </template>
          <div class="setting-desktop__toggles">
            <setting-toggle-row
              v-for="item in g.items"
              :key="item.key"
              :form="form"
              :item="item"
              dense
            />
          </div>
          <div v-if="g.id === 'invoice'" class="setting-desktop__extra">
            <v-select
              v-model="form.returnProductInquiryMode"
              :items="inquiryModes"
              :label="$t('returnProductInquiryMode')"
              item-text="text"
              item-value="value"
              outlined
              dense
              clearable
              hide-details
            />
          </div>
          <template #footer>
            <footer-actions />
          </template>
        </setting-panel>
      </template>

      <setting-panel
        v-if="investor && active === 'investor'"
        icon="mdi-handshake-outline"
        :title="$t('settingSection_investor')"
        :subtitle="$t('settingSection_investorHint')"
      >
        <div class="setting-desktop__grid setting-desktop__grid--2">
          <autocomplete
            v-model="form.brokerAccount"
            :title="$t('brokerAccount')"
            type="person"
            :value="form.brokerAccount"
          />
          <v-text-field
            v-model="form.brokerPercent"
            :label="$t('brokerPercent')"
            suffix="%"
            inputmode="decimal"
            hide-details
            outlined
            clearable
            dense
          />
          <autocomplete
            v-model="form.defaultCustomer"
            :title="$t('defaultCustomer')"
            type="person"
            :value="form.defaultCustomer"
          />
        </div>
        <template #footer>
          <footer-actions />
        </template>
      </setting-panel>

      <!-- Self-contained modules (save on their own) -->
      <template v-for="m in modules">
        <setting-panel
          v-if="active === m.id"
          :key="m.id"
          :icon="m.icon"
          :title="$t(m.title)"
          :fill="!!m.fill"
          :flush="!m.fill"
        >
          <component :is="m.component" />
        </setting-panel>
      </template>

      <setting-panel
        v-if="active === 'payment'"
        icon="mdi-credit-card-outline"
        :title="$t('paymentSetting')"
        fill
      >
        <template #tabs>
          <v-tabs v-model="paymentTab" height="44" show-arrows>
            <v-tab v-for="p in paymentModules" :key="p.id">
              <v-icon small class="me-1">{{ p.icon }}</v-icon
              >{{ $t(p.title) }}
            </v-tab>
          </v-tabs>
        </template>
        <component :is="paymentModules[paymentTab].component" :key="paymentTab" />
      </setting-panel>
    </section>
  </div>
</template>

<script>
import settingViewMixin from "./settingViewMixin";
import SettingPanel from "./SettingPanel";
import { VBtn, VSpacer } from "vuetify/lib";

export default {
  name: "SettingDesktop",
  components: {
    SettingPanel,
    // Shared save/cancel footer; reads loading state and emits through the parent.
    FooterActions: {
      inject: ["settingView"],
      render(h) {
        const view = this.settingView;
        return h("div", { class: "setting-desktop-foot" }, [
          // Start side (right in RTL): submit; end side: cancel
          h(
            VBtn,
            {
              props: { color: "primary", depressed: true, loading: view.loading },
              class: "app-form-action-btn",
              on: { click: () => view.$emit("save") },
            },
            view.$t("submit")
          ),
          h("span", { class: "setting-desktop-foot__hint" }, view.$t("settingSaveHint")),
          h(VSpacer),
          h(
            VBtn,
            {
              props: { color: "grey", outlined: true },
              class: "app-form-action-btn",
              on: { click: () => view.$emit("cancel") },
            },
            view.$t("cancel")
          ),
        ]);
      },
    },
  },
  mixins: [settingViewMixin],
  provide() {
    return { settingView: this };
  },
  data: () => ({
    active: "business",
    paymentTab: 0,
  }),
  computed: {
    navGroups() {
      const general = [
        {
          id: "business",
          icon: "mdi-storefront-outline",
          title: "settingSection_business",
        },
        { id: "details", icon: "mdi-book-open-variant", title: "settingSection_details" },
        ...this.groups.map((g) => ({
          id: g.id,
          icon: g.icon,
          title: g.title,
          count: this.countLabel(g),
        })),
      ];
      if (this.investor) {
        general.push({
          id: "investor",
          icon: "mdi-handshake-outline",
          title: "settingSection_investor",
        });
      }
      return [
        { id: "general", title: "general", items: general },
        {
          id: "other",
          title: "settingSection_more",
          items: [
            ...this.modules,
            {
              id: "payment",
              icon: "mdi-credit-card-outline",
              title: "paymentSetting",
              disabled: !this.gateway,
            },
          ],
        },
      ];
    },
    profileAvatarUrl() {
      const val = this.form.imagePickerVal || this.form.logo;
      if (!val) return null;
      return val.url || val.path || val.link || null;
    },
  },
  methods: {
    select(id) {
      this.active = id;
    },
  },
};
</script>

<style scoped>
.setting-desktop {
  display: flex;
  align-items: stretch;
  gap: 16px;
  height: 100%;
  min-height: 0;
}

/* Sidebar */
.setting-desktop__nav {
  flex: 0 0 256px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  overflow: hidden;
}
.theme--dark .setting-desktop__nav {
  background: #1e1e1e;
  border-color: rgba(255, 255, 255, 0.12);
}
.setting-desktop__profile {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}
.setting-desktop__profile-text {
  min-width: 0;
}
.setting-desktop__profile-name {
  font-weight: 700;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.setting-desktop__profile-sub {
  font-size: 0.75rem;
  opacity: 0.6;
  direction: ltr;
  text-align: end;
}
.setting-desktop__nav-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 8px 12px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 23, 42, 0.15) transparent;
}
.setting-desktop__nav-heading {
  font-size: 0.7rem;
  font-weight: 700;
  opacity: 0.5;
  padding: 12px 10px 4px;
}
.setting-desktop__nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 2px;
  padding: 9px 10px;
  border-radius: 9px;
  font-size: 0.84rem;
  text-align: start;
  color: inherit;
  transition: background-color 0.15s ease;
}
.setting-desktop__nav-item:hover:not(:disabled) {
  background: rgba(15, 23, 42, 0.04);
}
.setting-desktop__nav-item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.setting-desktop__nav-item--active,
.setting-desktop__nav-item--active:hover:not(:disabled) {
  background: rgba(25, 118, 210, 0.1);
  color: var(--v-primary-base, #1976d2);
  font-weight: 600;
}
.setting-desktop__nav-item--active .setting-desktop__nav-icon {
  color: inherit !important;
}
.setting-desktop__nav-label {
  flex: 1 1 auto;
}
.setting-desktop__nav-count {
  font-size: 0.7rem;
  padding: 1px 8px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.06);
  direction: ltr;
}

/* Content */
.setting-desktop__content {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}
.setting-desktop__business {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
}
.setting-desktop__logo {
  flex: 0 0 auto;
  align-self: flex-start;
  max-width: 100px;
}
.setting-desktop__business .setting-desktop__grid {
  flex: 1 1 auto;
}
.setting-desktop__section-label {
  font-size: 0.78rem;
  font-weight: 700;
  opacity: 0.55;
  margin-bottom: 12px;
}
.setting-desktop__grid {
  display: grid;
  gap: 30px 16px;
  align-items: start;
  padding-top: 8px;
}
.setting-desktop__grid--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.setting-desktop__grid--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.setting-desktop__span-all {
  grid-column: 1 / -1;
}
.setting-desktop__grid > * {
  margin: 0 !important;
  padding: 0 !important;
  min-width: 0;
}
.setting-desktop__grid ::v-deep .v-text-field__details {
  margin-bottom: 0 !important;
}
.setting-desktop__toggles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 10px;
}
.setting-desktop__extra {
  margin-top: 20px;
  max-width: 360px;
}

@media (max-width: 1100px) {
  .setting-desktop__grid--3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Footer actions (rendered by FooterActions inside SettingPanel's footer slot) */
.setting-desktop ::v-deep .setting-desktop-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.setting-desktop ::v-deep .setting-desktop-foot__hint {
  font-size: 0.78rem;
  opacity: 0.6;
}
</style>
